/**
 * Slug + wikilink helpers.
 *
 * These reproduce the Kip coop's slug rules so this client resolves
 * `[[wikilinks]]` to the same page filenames the desktop app does. The rules
 * are the obvious ones (lowercase, non-alphanumeric runs -> "-", trim); this
 * is an independent implementation for interoperability, not copied code.
 */

/** `"Dr. Alvarez"` -> `"dr-alvarez"`. Lowercase, non-alphanumeric runs -> `-`, trimmed. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** `"sleep-hygiene"` -> `"Sleep Hygiene"`. The inverse used for display titles. */
export function humanize(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const WIKILINK_RE = /\[\[([^\]]+)\]\]/g;

/** Every `[[wikilink]]` target in `text`, each normalized through `slugify()`. Not deduped. */
export function extractWikilinkSlugs(text: string): string[] {
  const slugs: string[] = [];
  let match: RegExpExecArray | null;
  WIKILINK_RE.lastIndex = 0;
  while ((match = WIKILINK_RE.exec(text)) !== null) {
    slugs.push(slugify(match[1]!));
  }
  return slugs;
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i]![j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1]![j - 1]!
          : 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!);
    }
  }
  return dp[m]![n]!;
}

/** Normalized Levenshtein similarity in [0,1] (1 = identical). */
export function slugSimilarity(a: string, b: string): number {
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length, 1);
}

/** Below this similarity, two slugs are treated as unrelated pages. */
export const SIMILARITY_THRESHOLD = 0.45;
